import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { cn } from "~/lib/utils";
import type { TimeSeriesItem } from "~/modules/asset_timeseries_v4/types";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_TOOLTIP_PROPS,
} from "~/modules/visualization/consts";
import { formatXAxisTick } from "~/modules/visualization/components/functions/formatXAxisTick";
import { RWA_COLORS } from "~/styles/colors";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";
import type { ScreenerAsset } from "./types";
import {
    getGroupedPerformanceChartData,
    type PerformanceChartSeries,
    type PerformanceGroup,
    type PerformancePeriod,
} from "./getPerformanceChartData";

export type {
    PerformanceGroup,
    PerformancePeriod,
} from "./getPerformanceChartData";

// // // //

const CHART_FONT_FAMILY =
    'Lausanne, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const PERFORMANCE_PERIOD_OPTIONS = [
    { label: "1M", value: "1M", testID: "performance-period--1m" },
    { label: "3M", value: "3M", testID: "performance-period--3m" },
    { label: "YTD", value: "YTD", testID: "performance-period--ytd" },
    { label: "1Y", value: "1Y", testID: "performance-period--1y" },
];

function formatPerformance(value: number): string {
    return `${value > 0 ? "+" : ""}${formatPercentValue(value)}%`;
}

interface PerformanceEndLabel {
    color: string;
    labelY: number;
    opacity: number;
    pointX: number;
    pointY: number;
    value: number;
}

const performanceEndLabelElements = new WeakMap<
    Highcharts.Chart,
    Highcharts.SVGElement[]
>();

function getEndLabelText(value: number): string {
    return formatPerformance(value);
}

function getEndLabelRailWidth(series: PerformanceChartSeries[]): number {
    const longestLabelLength = Math.max(
        ...series.map(
            (item) =>
                getEndLabelText(item.data[item.data.length - 1]?.[1] ?? 0)
                    .length,
        ),
    );

    return Math.min(96, Math.max(72, longestLabelLength * 6 + 20));
}

function getEndLabelColor(color: string, brightness: number): string {
    const adjustedColor = Highcharts.color(color).brighten(brightness).get();

    return typeof adjustedColor === "string" ? adjustedColor : color;
}

function destroyPerformanceEndLabels(chart: Highcharts.Chart) {
    performanceEndLabelElements
        .get(chart)
        ?.forEach((element) => element.destroy());
    performanceEndLabelElements.delete(chart);
    chart.container
        .querySelectorAll(
            ".performance-end-label, .performance-end-label-connector, .performance-end-label-marker",
        )
        .forEach((element) => element.remove());
}

function drawPerformanceEndLabels(chart: Highcharts.Chart) {
    destroyPerformanceEndLabels(chart);

    const labels = chart.series
        .map<PerformanceEndLabel | null>((chartSeries) => {
            const point = [...chartSeries.points]
                .reverse()
                .find(
                    (seriesPoint) =>
                        typeof seriesPoint.y === "number" &&
                        typeof seriesPoint.plotX === "number" &&
                        typeof seriesPoint.plotY === "number",
                );

            if (
                !chartSeries.visible ||
                !point ||
                typeof point.y !== "number" ||
                typeof point.plotX !== "number" ||
                typeof point.plotY !== "number"
            ) {
                return null;
            }

            return {
                color:
                    typeof chartSeries.color === "string"
                        ? chartSeries.color
                        : RWA_COLORS.slate["500"],
                labelY: 0,
                opacity: Number(chartSeries.options.opacity ?? 1),
                pointX: chart.plotLeft + point.plotX,
                pointY: chart.plotTop + point.plotY,
                value: point.y,
            };
        })
        .filter((label): label is PerformanceEndLabel => label !== null)
        .sort((a, b) => a.pointY - b.pointY);

    if (labels.length === 0) {
        return;
    }

    const labelHeight = 20;
    const labelGap = 3;
    const plotTop = chart.plotTop;
    const plotBottom = chart.plotTop + chart.plotHeight;
    let nextLabelY = plotTop;

    labels.forEach((label) => {
        label.labelY = Math.max(label.pointY - labelHeight / 2, nextLabelY);
        nextLabelY = label.labelY + labelHeight + labelGap;
    });

    const overflow =
        labels[labels.length - 1].labelY + labelHeight - plotBottom;
    if (overflow > 0) {
        labels.forEach((label) => {
            label.labelY -= overflow;
        });
    }

    if (labels[0].labelY < plotTop) {
        labels[0].labelY = plotTop;
        for (let index = 1; index < labels.length; index++) {
            labels[index].labelY = Math.max(
                labels[index].labelY,
                labels[index - 1].labelY + labelHeight + labelGap,
            );
        }
    }

    const labelX = chart.plotLeft + chart.plotWidth + 10;
    const elements = labels.flatMap((label) => {
        const connector = chart.renderer
            .path([
                ["M", label.pointX + 3, label.pointY],
                ["L", labelX - 4, label.labelY + labelHeight / 2],
            ])
            .addClass("performance-end-label-connector")
            .attr({
                opacity: label.opacity < 1 ? 0.18 : 0.65,
                stroke: label.color,
                "stroke-width": 1,
                zIndex: 6,
            })
            .add();
        const valueTag = chart.renderer
            .label(getEndLabelText(label.value), labelX, label.labelY, "rect")
            .addClass("performance-end-label")
            .attr({
                fill: getEndLabelColor(label.color, -0.32),
                opacity: 1,
                padding: 4,
                r: 4,
                stroke: getEndLabelColor(label.color, -0.42),
                "stroke-width": 1,
                zIndex: 7,
            })
            .css({
                color: "#fff",
                fontFamily: CHART_FONT_FAMILY,
                fontSize: "11px",
                fontWeight: "500",
                fontVariantNumeric: "tabular-nums",
                pointerEvents: "none",
            })
            .add();

        return [connector, valueTag];
    });

    performanceEndLabelElements.set(chart, elements);
}

interface PerformanceChartOptions {
    height: number;
    hiddenSeriesIDs: Record<number, boolean>;
    highlightedSeriesID?: number | null;
    onHighlightedSeriesChange?: (seriesID: number | null) => void;
    series: PerformanceChartSeries[];
    showEndLabels: boolean;
    showLegend: boolean;
}

function getChartOptions(props: PerformanceChartOptions): Highcharts.Options {
    const {
        height,
        hiddenSeriesIDs,
        highlightedSeriesID,
        onHighlightedSeriesChange,
        series,
        showEndLabels,
        showLegend,
    } = props;

    return {
        chart: {
            type: "line",
            height,
            backgroundColor: "transparent",
            animation: false,
            marginRight: showEndLabels
                ? getEndLabelRailWidth(series)
                : undefined,
            spacingTop: showLegend ? 24 : 12,
            style: {
                fontFamily: CHART_FONT_FAMILY,
            },
            events: showEndLabels
                ? {
                      render: function () {
                          drawPerformanceEndLabels(this);
                      },
                  }
                : undefined,
        },
        title: { text: undefined },
        credits: { enabled: false },
        accessibility: { enabled: false },
        legend: {
            enabled: showLegend,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            margin: 16,
            padding: 0,
            itemDistance: 20,
            itemMarginBottom: 4,
            itemWidth: 160,
            useHTML: true,
            squareSymbol: false,
            symbolHeight: 0,
            symbolRadius: 0,
            symbolWidth: 0,
            itemStyle: {
                color: "var(--foreground)",
                cursor: "pointer",
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif',
                fontSize: "0.8em",
                fontWeight: "400",
            },
            itemHoverStyle: {
                color: "var(--foreground)",
            },
            itemHiddenStyle: {
                color: "var(--muted-foreground)",
            },
            navigation: {
                activeColor: "var(--foreground)",
                inactiveColor: "var(--muted-foreground)",
            },
            labelFormatter: function (this: any) {
                return `<div class="flex items-center gap-2">
                    <div class="h-3 w-3 min-w-3 rounded-full" style="background-color: ${this.color};"></div>
                    <span style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${this.name}</span>
                </div>`;
            },
        },
        xAxis: {
            ...(DEFAULT_AXIS_PROPS as any),
            type: "datetime",
            gridLineWidth: 0,
            lineColor: "var(--border)",
            tickColor: "var(--border)",
            tickLength: 4,
            tickPixelInterval: 110,
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                formatter: function () {
                    return formatXAxisTick(new Date(Number(this.value)));
                },
            },
        },
        yAxis: {
            ...(DEFAULT_AXIS_PROPS as any),
            endOnTick: false,
            gridLineColor: "var(--border)",
            lineColor: "var(--border)",
            maxPadding: 0.16,
            minPadding: 0.12,
            tickColor: "var(--border)",
            title: { text: undefined },
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                formatter: function () {
                    return `${formatPercentValue(Number(this.value))}%`;
                },
            },
            plotLines: [
                {
                    color: "var(--border)",
                    dashStyle: "ShortDash",
                    value: 0,
                    width: 1,
                    zIndex: 2,
                },
            ],
        },
        tooltip: {
            useHTML: true,
            shared: true,
            outside: true,
            distance: 20,
            backgroundColor: "var(--card)",
            style: { color: "var(--foreground)", zIndex: 250 },
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const points = [...(this.points ?? [])].sort(
                    (a: any, b: any) => b.y - a.y,
                );

                return [
                    '<div class="flex flex-col select-none p-2 min-w-72">',
                    '<div class="flex items-center justify-between mb-2">',
                    `<p class="text-chart-lg tabular-nums tracking-wider">${formatXAxisTick(new Date(this.x))}</p>`,
                    "</div>",
                    ...points.map(
                        (point: any) =>
                            '<div class="flex justify-between">' +
                            '<p class="flex text-chart-body items-center">' +
                            `<span class="h-3 w-3 rounded-full mr-2" style="background-color:${point.color}"></span>` +
                            `<span class="mr-2">${point.series.name}</span>` +
                            "</p>" +
                            '<p class="flex items-center text-chart-body ml-4">' +
                            `<span class="font-mono">${formatPerformance(point.y)}</span>` +
                            "</p>" +
                            "</div>",
                    ),
                    "</div>",
                ].join("");
            },
        },
        plotOptions: {
            series: {
                animation: false,
                lineWidth: 2,
                marker: { enabled: false },
                states: {
                    hover: { lineWidthPlus: 1 },
                },
            },
        },
        series: series.map((item) => {
            const isHighlighted = highlightedSeriesID === item.id;
            const isDimmed =
                highlightedSeriesID !== null &&
                highlightedSeriesID !== undefined &&
                !isHighlighted;

            return {
                type: "line",
                id: String(item.id),
                name: item.name,
                color: isDimmed ? RWA_COLORS.slate["300"] : item.color,
                data: item.data,
                lineWidth: isDimmed ? 1.5 : isHighlighted ? 3 : 2,
                opacity: 1,
                visible: !hiddenSeriesIDs[item.id],
                zIndex: isHighlighted ? 2 : 1,
                events: {
                    mouseOver: () => onHighlightedSeriesChange?.(item.id),
                    mouseOut: () => onHighlightedSeriesChange?.(null),
                },
            };
        }),
    };
}

const EMPTY_HIDDEN_SERIES_IDS: Record<number, boolean> = {};

interface AssetPerformanceChartProps {
    assets: ScreenerAsset[];
    className?: string;
    group: PerformanceGroup;
    height?: number;
    hiddenSeriesIDs?: Record<number, boolean>;
    highlightedSeriesID?: number | null;
    onHighlightedSeriesChange?: (seriesID: number | null) => void;
    performanceByAssetClass: TimeSeriesItem[];
    performanceByManager: TimeSeriesItem[];
    period: PerformancePeriod;
    showEndLabels?: boolean;
    showLegend?: boolean;
}

function useAssetPerformanceChart(props: AssetPerformanceChartProps) {
    const {
        assets,
        group,
        height = 430,
        hiddenSeriesIDs = EMPTY_HIDDEN_SERIES_IDS,
        highlightedSeriesID,
        onHighlightedSeriesChange,
        performanceByAssetClass,
        performanceByManager,
        period,
        showEndLabels = false,
        showLegend = true,
    } = props;
    const isAssetClassGroup = group === "assetClass";
    const chartData = useMemo(
        () =>
            getGroupedPerformanceChartData({
                assets,
                group,
                performanceByAssetClass,
                performanceByManager,
                period,
            }),
        [assets, group, performanceByAssetClass, performanceByManager, period],
    );
    const options = useMemo(
        () =>
            getChartOptions({
                height,
                hiddenSeriesIDs,
                highlightedSeriesID,
                onHighlightedSeriesChange,
                series: chartData.series,
                showEndLabels,
                showLegend,
            }),
        [
            chartData.series,
            height,
            hiddenSeriesIDs,
            highlightedSeriesID,
            onHighlightedSeriesChange,
            showEndLabels,
            showLegend,
        ],
    );

    return { chartData, isAssetClassGroup, options };
}

function AssetPerformanceChartContent(
    props: Pick<AssetPerformanceChartProps, "className" | "period"> & {
        height: number;
        isAssetClassGroup: boolean;
        options: Highcharts.Options;
    },
) {
    const { className, height, isAssetClassGroup, options, period } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined") {
            return;
        }

        let animationFrame: number | null = null;
        const resizeObserver = new ResizeObserver(() => {
            if (animationFrame !== null) {
                window.cancelAnimationFrame(animationFrame);
            }

            animationFrame = window.requestAnimationFrame(() => {
                chartRef.current?.chart.reflow();
            });
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            if (animationFrame !== null) {
                window.cancelAnimationFrame(animationFrame);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            role="img"
            aria-label={`Time series chart comparing average ${period} percentage performance by ${isAssetClassGroup ? "asset class" : "asset manager"}.`}
            className={cn("w-full min-w-0 overflow-hidden", className)}
        >
            <div className="w-full min-w-0">
                <WindowResizeObserver
                    defaultPlaceholderHeight={height}
                    delay={1200}
                >
                    <HighchartsReact
                        ref={chartRef}
                        highcharts={Highcharts}
                        options={options}
                        containerProps={{
                            className: "w-full min-w-0",
                        }}
                    />
                </WindowResizeObserver>
            </div>
        </div>
    );
}

export function AssetPerformanceChart(props: AssetPerformanceChartProps) {
    const { className, height = 430, period } = props;
    const { chartData, isAssetClassGroup, options } =
        useAssetPerformanceChart(props);

    if (chartData.series.length === 0) {
        return null;
    }

    return (
        <AssetPerformanceChartContent
            className={className}
            height={height}
            isAssetClassGroup={isAssetClassGroup}
            options={options}
            period={period}
        />
    );
}
